/* Sprint 2N Phase 2 — Admin "Medarbejdere" tab.
   Mounts inside AdminDashboardPage. Only admin can use this (admin token required). */
import React from "react";
import { toast } from "sonner";
import {
  Plus, Edit2, RotateCcw, ShieldOff, ShieldCheck, Loader2, Copy,
  Mail, User, Lock, X, Check, Search, Upload, Download, FileText,
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  owner_admin: "Owner / Admin",
  admin: "Admin",
  operations_employee: "Operations",
  finance_employee: "Finance",
  support_employee: "Support",
};
const ROLE_OPTIONS = [
  "operations_employee", "finance_employee", "support_employee", "admin", "owner_admin",
];

function adminApi() {
  const token = localStorage.getItem("ema_admin_token");
  return axios.create({
    baseURL: `${API}/api`,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export default function EmployeeManagementPanel() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [q, setQ] = React.useState("");
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editEmp, setEditEmp] = React.useState(null);
  const [tempPwModal, setTempPwModal] = React.useState(null); // { email, temporary_password, login_url }
  const [bulkOpen, setBulkOpen] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi().get("/admin/employees");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Kunne ikke hente medarbejdere");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { load(); }, []);

  const onDisable = async (emp) => {
    if (!window.confirm(`Deaktivér ${emp.email}?\n\nMedarbejderen kan ikke logge ind, men data bevares til audit-formål.`)) return;
    try {
      await adminApi().delete(`/admin/employees/${emp.id}`);
      toast.success("Medarbejder deaktiveret");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke deaktivere");
    }
  };

  const onEnable = async (emp) => {
    try {
      await adminApi().patch(`/admin/employees/${emp.id}`, { active: true });
      toast.success("Medarbejder aktiveret");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke aktivere");
    }
  };

  const onResetPassword = async (emp) => {
    if (!window.confirm(`Nulstil password for ${emp.email}?\n\nMedarbejderen vil blive tvunget til at skifte password ved næste login.`)) return;
    try {
      const r = await adminApi().post(`/admin/employees/${emp.id}/reset-password`);
      setTempPwModal({
        email: emp.email,
        temporary_password: r.data.temporary_password,
        login_url: r.data.login_url,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke nulstille password");
    }
  };

  const filtered = items.filter((e) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (e.email || "").toLowerCase().includes(s)
        || (e.name || "").toLowerCase().includes(s)
        || (e.role || "").toLowerCase().includes(s);
  });

  return (
    <div data-testid="employee-management-panel">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display font-semibold text-2xl text-brand-dark">Medarbejdere</h2>
          <p className="text-brand-text2 text-sm mt-1">
            Opret, deaktivér og nulstil password for EMA Kurer medarbejdere.
            {" "}Loginside: <code className="bg-brand-bg2 px-1.5 py-0.5 text-xs">/employee/login</code>
          </p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-text2"/>
            <input
              type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Søg navn/email/rolle"
              data-testid="employee-search"
              className="pl-9 pr-3 py-2 border border-brand text-sm focus:border-brand-red focus:outline-none"
            />
          </div>
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="employee-create-btn"
            className="bg-brand-red hover:bg-brand-red/90 text-white px-4 py-2 text-sm font-bold tracking-wider uppercase flex items-center gap-2"
          >
            <Plus size={14}/> Opret medarbejder
          </button>
          <button
            onClick={() => setBulkOpen(true)}
            data-testid="employee-bulk-import-btn"
            className="bg-brand-dark hover:bg-brand-dark/90 text-white px-4 py-2 text-sm font-bold tracking-wider uppercase flex items-center gap-2"
          >
            <Upload size={14}/> Importér CSV
          </button>
          <button
            onClick={downloadCsvTemplate}
            data-testid="employee-csv-template-btn"
            className="bg-white border border-brand text-brand-dark hover:bg-brand-bg2 px-4 py-2 text-sm font-bold tracking-wider uppercase flex items-center gap-2"
          >
            <Download size={14}/> Skabelon
          </button>
        </div>
      </div>

      {loading && <div className="text-brand-text2 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Indlæser...</div>}

      {!loading && (
        <div className="bg-white border border-brand overflow-x-auto">
          <table className="w-full text-sm" data-testid="employees-table">
            <thead className="bg-brand-bg2 text-brand-text2 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Navn</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Rolle</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Skift PW</th>
                <th className="text-left p-3">Sidst logget ind</th>
                <th className="text-left p-3">Oprettet</th>
                <th className="text-right p-3">Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-brand-text2">
                  {q ? "Ingen medarbejdere matcher søgningen" : "Ingen medarbejdere endnu. Opret den første nu."}
                </td></tr>
              )}
              {filtered.map((emp) => (
                <tr key={emp.id} className="border-t border-brand hover:bg-brand-bg2" data-testid={`employee-row-${emp.id}`}>
                  <td className="p-3 font-semibold text-brand-dark">{emp.name || "—"}</td>
                  <td className="p-3 text-brand-text2">{emp.email}</td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${
                      emp.role === "owner_admin" || emp.role === "admin" ? "bg-brand-red/10 text-brand-red" :
                      emp.role === "finance_employee" ? "bg-amber-100 text-amber-800" :
                      emp.role === "operations_employee" ? "bg-blue-100 text-blue-800" :
                      "bg-slate-100 text-slate-700"
                    }`}>
                      {ROLE_LABELS[emp.role] || emp.role}
                    </span>
                  </td>
                  <td className="p-3">
                    {emp.active !== false
                      ? <span className="inline-flex items-center gap-1 text-green-700 text-xs font-bold"><Check size={12}/> Aktiv</span>
                      : <span className="inline-flex items-center gap-1 text-red-700 text-xs font-bold"><X size={12}/> Deaktiveret</span>}
                  </td>
                  <td className="p-3 text-xs">
                    {emp.must_change_password ? <span className="text-amber-700 font-bold">JA</span> : "—"}
                  </td>
                  <td className="p-3 text-xs text-brand-text2">{emp.last_login_iso ? new Date(emp.last_login_iso).toLocaleString("da-DK") : "Aldrig"}</td>
                  <td className="p-3 text-xs text-brand-text2">{emp.created_at_iso ? new Date(emp.created_at_iso).toLocaleDateString("da-DK") : "—"}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={() => setEditEmp(emp)} data-testid={`employee-edit-${emp.id}`} title="Rediger" className="p-1.5 hover:bg-brand-bg2"><Edit2 size={14}/></button>
                    <button onClick={() => onResetPassword(emp)} data-testid={`employee-reset-pw-${emp.id}`} title="Nulstil password" className="p-1.5 hover:bg-brand-bg2"><RotateCcw size={14}/></button>
                    {emp.active !== false
                      ? <button onClick={() => onDisable(emp)} data-testid={`employee-disable-${emp.id}`} title="Deaktivér" className="p-1.5 hover:bg-red-50 text-red-700"><ShieldOff size={14}/></button>
                      : <button onClick={() => onEnable(emp)} data-testid={`employee-enable-${emp.id}`} title="Aktivér" className="p-1.5 hover:bg-green-50 text-green-700"><ShieldCheck size={14}/></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-4 text-xs text-brand-text2">
        🔒 Backend håndhæver rolle-tilladelser. Operations får 403 på finance-endpoints, finance får 403 på operational alerts. Audit-log er aktiv.
        Se permission-map: <code className="bg-brand-bg2 px-1.5 py-0.5">GET /api/admin/employees/roles</code>
      </p>

      {createOpen && (
        <CreateEmployeeModal
          onClose={() => setCreateOpen(false)}
          onCreated={(empWithPw) => {
            setCreateOpen(false);
            setTempPwModal(empWithPw);
            load();
          }}
        />
      )}

      {editEmp && (
        <EditEmployeeModal
          emp={editEmp}
          onClose={() => setEditEmp(null)}
          onSaved={() => { setEditEmp(null); load(); }}
        />
      )}

      {tempPwModal && (
        <TempPasswordModal data={tempPwModal} onClose={() => setTempPwModal(null)}/>
      )}

      {bulkOpen && (
        <BulkImportModal
          onClose={() => setBulkOpen(false)}
          onCompleted={() => { setBulkOpen(false); load(); }}
        />
      )}
    </div>
  );
}

// CSV template download — module-level so the button handler can call it
const CSV_TEMPLATE_HEADERS = "name,email,phone,role,active,temporary_password,force_password_change,preferred_language,vehicle_type,area_preference,max_shifts_per_week,max_hours_per_day";
const CSV_TEMPLATE_EXAMPLE = "Test Driver,testdriver@example.com,4512345678,driver,true,,true,bilingual,car,Sjælland,5,8";

function downloadCsvTemplate() {
  const csv = CSV_TEMPLATE_HEADERS + "\n" + CSV_TEMPLATE_EXAMPLE + "\n";
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "ema-medarbejdere-skabelon.csv";
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
  toast.success("CSV-skabelon downloadet");
}

function parseCsv(text) {
  // Simple CSV parser supporting quoted fields and Danish line endings
  const lines = text.replace(/\r\n?/g, "\n").split("\n").filter((l) => l.trim().length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };
  const parseLine = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; continue; }
      if (c === '"') { inQ = !inQ; continue; }
      if (c === "," && !inQ) { out.push(cur); cur = ""; continue; }
      cur += c;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  const headers = parseLine(lines[0]).map((h) => h.toLowerCase().trim());
  const rows = lines.slice(1).map((l) => {
    const cols = parseLine(l);
    const row = {};
    headers.forEach((h, i) => { row[h] = cols[i] != null ? cols[i] : ""; });
    return row;
  });
  return { headers, rows };
}

function BulkImportModal({ onClose, onCompleted }) {
  const [file, setFile] = React.useState(null);
  const [parsedRows, setParsedRows] = React.useState([]);
  const [preview, setPreview] = React.useState(null);
  const [importing, setImporting] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null); setResult(null);
    const text = await f.text();
    const { rows } = parseCsv(text);
    if (rows.length === 0) {
      toast.error("CSV indeholder ingen datalinjer");
      return;
    }
    if (rows.length > 500) {
      toast.error("Maks 500 rækker pr. import — del filen op");
      return;
    }
    setParsedRows(rows);
    // Auto-trigger dry-run preview
    runPreview(rows);
  };

  const runPreview = async (rows) => {
    setImporting(true);
    try {
      const r = await adminApi().post("/admin/employees/bulk-import", { rows, dry_run: true });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validering fejlede");
    } finally { setImporting(false); }
  };

  const onCommit = async () => {
    if (!parsedRows.length) return;
    if (!preview) {
      toast.error("Validering mangler");
      return;
    }
    if (preview.valid === 0 && preview.skipped === 0) {
      toast.error("Ingen rækker at behandle");
      return;
    }
    setImporting(true);
    try {
      const r = await adminApi().post("/admin/employees/bulk-import", { rows: parsedRows, dry_run: false });
      setResult(r.data);
      toast.success(`${r.data.created} oprettet · ${r.data.skipped} sprunget over · ${r.data.errors} fejl`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import fejlede");
    } finally { setImporting(false); }
  };

  const downloadResult = () => {
    if (!result) return;
    const headers = ["row_num", "email", "name", "role", "status", "reason", "temporary_password"];
    const lines = [headers.join(",")];
    result.rows.forEach((r) => {
      lines.push(headers.map((h) => {
        const v = r[h] != null ? String(r[h]) : "";
        return v.includes(",") || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
      }).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `ema-bulk-import-resultat-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="bulk-import-modal">
      <div className="bg-white border border-brand max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="bg-brand-dark text-white px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div className="font-display font-bold flex items-center gap-2"><Upload size={18}/> Bulk import — Medarbejdere / Chauffører</div>
          <button onClick={onClose} data-testid="bulk-import-close"><X size={18}/></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {!file && !result && (
            <div className="space-y-4">
              <p className="text-sm text-brand-text2">
                Upload en CSV-fil med medarbejdere og/eller chauffører. Påkrævede kolonner: <b>name, email, role</b>.
                {" "}Maks 500 rækker pr. import. Tilladte roller: <code className="text-xs bg-brand-bg2 px-1.5 py-0.5">operations_employee · finance_employee · support_employee · admin · driver · subcontractor_driver</code>.
              </p>
              <button onClick={downloadCsvTemplate} data-testid="bulk-template-link" className="text-sm text-brand-red hover:underline flex items-center gap-1">
                <Download size={12}/> Download CSV-skabelon
              </button>
              <label className="block border-2 border-dashed border-brand p-8 text-center cursor-pointer hover:border-brand-red transition" data-testid="bulk-csv-dropzone">
                <FileText size={32} className="mx-auto text-brand-text2 mb-3"/>
                <div className="text-sm text-brand-dark font-bold">Vælg CSV-fil</div>
                <div className="text-xs text-brand-text2 mt-1">Eller træk filen hertil</div>
                <input type="file" accept=".csv,text/csv" onChange={onFileChange} className="hidden" data-testid="bulk-csv-file"/>
              </label>
            </div>
          )}

          {importing && <div className="text-center py-8 text-brand-text2 flex items-center justify-center gap-2"><Loader2 className="animate-spin" size={16}/> Behandler...</div>}

          {file && preview && !result && !importing && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3" data-testid="bulk-preview-summary">
                <Stat label="Total" value={preview.total}/>
                <Stat label="Gyldige" value={preview.valid} color="text-green-700"/>
                <Stat label="Springes over" value={preview.skipped} color="text-amber-700"/>
                <Stat label="Fejl" value={preview.errors} color="text-red-700"/>
              </div>

              <div className="border border-brand max-h-96 overflow-y-auto">
                <table className="w-full text-xs" data-testid="bulk-preview-table">
                  <thead className="bg-brand-bg2 text-brand-text2 uppercase sticky top-0">
                    <tr>
                      <th className="text-left p-2">#</th>
                      <th className="text-left p-2">Navn</th>
                      <th className="text-left p-2">Email</th>
                      <th className="text-left p-2">Rolle</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r) => (
                      <tr key={r.row_num} className={`border-t border-brand ${r.status === "error" ? "bg-red-50" : r.status === "skipped" ? "bg-amber-50" : ""}`} data-testid={`bulk-preview-row-${r.row_num}`}>
                        <td className="p-2 text-brand-text2">{r.row_num}</td>
                        <td className="p-2">{r.name}</td>
                        <td className="p-2 text-brand-text2">{r.email}</td>
                        <td className="p-2 text-xs">{r.role}</td>
                        <td className="p-2">
                          {r.status === "valid" && <span className="text-green-700 font-bold">✓ Gyldig</span>}
                          {r.status === "skipped" && <span className="text-amber-700 font-bold">⊘ Spring over</span>}
                          {r.status === "error" && <span className="text-red-700 font-bold">✗ Fejl</span>}
                        </td>
                        <td className="p-2 text-brand-text2">{r.reason || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 p-2">
                ⚠️ Bekræft før import — kun gyldige rækker oprettes. Email-duplikater springes over (vi overskriver ALDRIG eksisterende).
              </p>

              <div className="flex justify-end gap-3 pt-2">
                <button onClick={onClose} className="px-4 py-2 text-sm text-brand-text2">Annullér</button>
                <button onClick={onCommit} disabled={preview.valid === 0 && preview.skipped === 0} data-testid="bulk-commit-btn" className="bg-brand-red hover:bg-brand-red/90 text-white px-5 py-2 text-sm font-bold tracking-wider uppercase disabled:opacity-50">
                  {preview.valid > 0
                    ? `Bekræft og importér ${preview.valid} ${preview.valid === 1 ? "række" : "rækker"}`
                    : `Vis resultat (${preview.skipped} sprunget over)`}
                </button>
              </div>
            </div>
          )}

          {result && (
            <div className="space-y-4" data-testid="bulk-result">
              <div className="grid grid-cols-4 gap-3">
                <Stat label="Total" value={result.total}/>
                <Stat label="Oprettet" value={result.created} color="text-green-700"/>
                <Stat label="Sprunget over" value={result.skipped} color="text-amber-700"/>
                <Stat label="Fejl" value={result.errors} color="text-red-700"/>
              </div>

              {result.rows.some((r) => r.temporary_password) ? (
                <div className="bg-amber-50 border-2 border-amber-300 p-3">
                  <p className="text-sm font-bold text-amber-900 mb-2">🔐 Midlertidige passwords — vises kun denne ene gang!</p>
                  <p className="text-xs text-amber-800 mb-2">Download CSV nu eller kopiér til sikker placering. Disse vises aldrig igen.</p>
                  <button onClick={downloadResult} data-testid="bulk-download-result" className="bg-amber-700 hover:bg-amber-800 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2">
                    <Download size={12}/> Download resultat-CSV (inkl. passwords)
                  </button>
                </div>
              ) : (
                <div className="bg-brand-bg2 border border-brand p-3">
                  <p className="text-sm font-bold text-brand-dark mb-2">📄 Resultat-log</p>
                  <p className="text-xs text-brand-text2 mb-2">Download log over sprunget over/fejlede rækker (ingen passwords).</p>
                  <button onClick={downloadResult} data-testid="bulk-download-result" className="bg-brand-dark hover:bg-brand-dark/90 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2">
                    <Download size={12}/> Download resultat-CSV
                  </button>
                </div>
              )}

              <div className="border border-brand max-h-96 overflow-y-auto">
                <table className="w-full text-xs" data-testid="bulk-result-table">
                  <thead className="bg-brand-bg2 text-brand-text2 uppercase sticky top-0">
                    <tr><th className="text-left p-2">#</th><th className="text-left p-2">Email</th><th className="text-left p-2">Status</th><th className="text-left p-2">Note / Password</th></tr>
                  </thead>
                  <tbody>
                    {result.rows.map((r) => (
                      <tr key={r.row_num} className="border-t border-brand">
                        <td className="p-2">{r.row_num}</td>
                        <td className="p-2">{r.email}</td>
                        <td className="p-2">
                          {r.status === "created" && <span className="text-green-700 font-bold">✓ Oprettet</span>}
                          {r.status === "skipped" && <span className="text-amber-700 font-bold">⊘ Skipped</span>}
                          {r.status === "error" && <span className="text-red-700 font-bold">✗ Fejl</span>}
                        </td>
                        <td className="p-2 text-brand-text2">
                          {r.temporary_password
                            ? <code className="bg-brand-bg2 px-1.5 py-0.5 font-mono text-[11px]">{r.temporary_password}</code>
                            : r.reason || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button onClick={onCompleted} data-testid="bulk-result-close" className="bg-brand-dark text-white px-5 py-2 text-sm font-bold tracking-wider uppercase">
                  Luk og opdater liste
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color = "text-brand-dark" }) {
  return (
    <div className="bg-brand-bg2 border border-brand p-3 text-center">
      <div className="text-xs uppercase text-brand-text2 tracking-wider">{label}</div>
      <div className={`font-display font-bold text-2xl mt-1 ${color}`}>{value}</div>
    </div>
  );
}

// ============================================
// Sub-components
// ============================================

function CreateEmployeeModal({ onClose, onCreated }) {
  const [form, setForm] = React.useState({ name: "", email: "", role: "operations_employee", temporary_password: "" });
  const [submitting, setSubmitting] = React.useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name || !form.role) {
      toast.error("Udfyld alle felter");
      return;
    }
    setSubmitting(true);
    try {
      const body = { email: form.email.trim().toLowerCase(), name: form.name.trim(), role: form.role };
      if (form.temporary_password.trim().length >= 10) {
        body.temporary_password = form.temporary_password.trim();
      }
      const r = await adminApi().post("/admin/employees", body);
      toast.success("Medarbejder oprettet");
      onCreated(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke oprette");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="employee-create-modal">
      <div className="bg-white border border-brand max-w-md w-full">
        <div className="bg-brand-dark text-white px-5 py-4 flex items-center justify-between">
          <div className="font-display font-bold">Opret medarbejder</div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <form onSubmit={onSubmit} className="p-5 space-y-4">
          <Field icon={User} label="Navn" v={form.name} setV={(v) => setForm((f) => ({ ...f, name: v }))} testid="employee-create-name"/>
          <Field icon={Mail} label="Email" type="email" v={form.email} setV={(v) => setForm((f) => ({ ...f, email: v }))} testid="employee-create-email"/>
          <div>
            <label className="block text-xs font-bold tracking-widest uppercase text-brand-text2 mb-2">Rolle</label>
            <select
              value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              data-testid="employee-create-role"
              className="w-full border border-brand px-3 py-2.5 text-sm focus:border-brand-red focus:outline-none"
            >
              {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
            </select>
            <p className="text-xs text-brand-text2 mt-1">Standard: Operations. Owner/Admin har fuld adgang — brug med omhu.</p>
          </div>
          <Field icon={Lock} label="Midlertidigt password (valgfrit — system genererer hvis tom)"
                 v={form.temporary_password} setV={(v) => setForm((f) => ({ ...f, temporary_password: v }))}
                 type="text" testid="employee-create-temp-pw"
                 hint="Min. 10 tegn hvis udfyldt"/>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2">
            🔐 Medarbejderen tvinges til at skifte password ved første login (must_change_password=true).
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-brand-text2 hover:text-brand-dark">Annullér</button>
            <button type="submit" disabled={submitting} data-testid="employee-create-submit" className="bg-brand-red hover:bg-brand-red/90 text-white px-5 py-2 text-sm font-bold tracking-wider uppercase disabled:opacity-50">
              {submitting ? <Loader2 className="animate-spin" size={14}/> : "Opret"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditEmployeeModal({ emp, onClose, onSaved }) {
  const [form, setForm] = React.useState({
    name: emp.name || "",
    role: emp.role,
    active: emp.active !== false,
  });
  const [forceChangePw, setForceChangePw] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await adminApi().patch(`/admin/employees/${emp.id}`, form);
      if (forceChangePw) {
        await adminApi().post(`/admin/employees/${emp.id}/reset-password`);
        toast.success("Medarbejder opdateret. Password er nulstillet — bed admin om at give nyt password til medarbejderen.");
      } else {
        toast.success("Medarbejder opdateret");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke gemme");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="employee-edit-modal">
      <div className="bg-white border border-brand max-w-md w-full">
        <div className="bg-brand-dark text-white px-5 py-4 flex items-center justify-between">
          <div className="font-display font-bold">Rediger {emp.email}</div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <form onSubmit={onSubmit} className="p-5 space-y-4">
          <Field icon={User} label="Navn" v={form.name} setV={(v) => setForm((f) => ({ ...f, name: v }))} testid="employee-edit-name"/>
          <div>
            <label className="block text-xs font-bold tracking-widest uppercase text-brand-text2 mb-2">Rolle</label>
            <select
              value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              data-testid="employee-edit-role"
              className="w-full border border-brand px-3 py-2.5 text-sm focus:border-brand-red focus:outline-none"
            >
              {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} data-testid="employee-edit-active"/>
            Aktiv (medarbejder kan logge ind)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={forceChangePw} onChange={(e) => setForceChangePw(e.target.checked)} data-testid="employee-edit-force-pw"/>
            Tving password-skift ved næste login (nulstiller password)
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-brand-text2">Annullér</button>
            <button type="submit" disabled={submitting} data-testid="employee-edit-submit" className="bg-brand-red hover:bg-brand-red/90 text-white px-5 py-2 text-sm font-bold tracking-wider uppercase disabled:opacity-50">
              {submitting ? <Loader2 className="animate-spin" size={14}/> : "Gem"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TempPasswordModal({ data, onClose }) {
  const copy = () => {
    navigator.clipboard?.writeText(data.temporary_password);
    toast.success("Kopieret til clipboard");
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="temp-password-modal">
      <div className="bg-white border border-brand max-w-md w-full">
        <div className="bg-amber-100 border-b border-amber-300 px-5 py-4 flex items-center gap-3">
          <Lock size={18} className="text-amber-800"/>
          <div className="font-display font-bold text-amber-900">Midlertidigt password</div>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-brand-text2">
            Giv dette password til <b className="text-brand-dark">{data.email}</b>. Det vises kun denne ene gang.
            Medarbejderen tvinges til at skifte det ved første login.
          </p>
          <div className="bg-brand-bg2 border border-brand p-3 flex items-center justify-between">
            <code className="font-mono text-lg text-brand-dark" data-testid="temp-password-value">{data.temporary_password}</code>
            <button onClick={copy} data-testid="temp-password-copy" className="text-brand-red hover:text-brand-red/80" title="Kopier"><Copy size={16}/></button>
          </div>
          <div className="text-xs text-brand-text2">
            Login URL: <code className="bg-brand-bg2 px-1.5 py-0.5">{data.login_url}</code>
          </div>
          <button onClick={onClose} data-testid="temp-password-close" className="w-full bg-brand-dark text-white py-2 text-sm font-bold tracking-wider uppercase">
            Luk
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, type = "text", v, setV, testid, hint }) {
  return (
    <div>
      <label className="block text-xs font-bold tracking-widest uppercase text-brand-text2 mb-2">
        {Icon && <Icon size={12} className="inline mr-1.5"/>} {label}
      </label>
      <input
        type={type} value={v} onChange={(e) => setV(e.target.value)}
        data-testid={testid}
        className="w-full border border-brand px-3 py-2.5 text-sm focus:border-brand-red focus:outline-none"
      />
      {hint && <p className="text-xs text-brand-text2 mt-1">{hint}</p>}
    </div>
  );
}
